/**
 * FIN-12176: a variant's `args.applicableTo` is not always a real array.
 * Some uploaded/cloned typologies persist it as a stringified array
 * (e.g. '["transaction"]') or an empty string. The Functions tab does
 * `applicableTo.reduce(...)`, which throws "e.reduce is not a function" on a
 * string. Normalize every shape to an array so consumers can safely iterate.
 *
 * @param {*} applicableTo  array | JSON-array string | comma string | empty | null
 * @param {string[]} fallback  returned when applicableTo is empty/uninterpretable
 * @returns {string[]}
 */
export const normalizeApplicableTo = (applicableTo, fallback = []) => {
  if (Array.isArray(applicableTo)) {
    return applicableTo;
  }
  if (typeof applicableTo === 'string') {
    const trimmed = applicableTo.trim();
    if (trimmed === '') {
      return fallback;
    }
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        // not valid JSON — fall through to comma-split
      }
    }
    return trimmed
      .split(',')
      .map((token) => token.trim())
      .filter(Boolean);
  }
  return fallback;
};

/**
 * FIN-13484: Group By candidates for one aggregation function. A grouping key must be a native
 * column of EVERY event class the function aggregates (its `applicableTo`), so this is the
 * intersection of the per-class option lists, in the first class's order. The BE
 * (`FraudAggregationFunction.validateWarnings`) enforces the same rule.
 *
 * @param {{transaction?: object[], activity?: object[]}} groupByOptions  per-class options
 * @param {string[]} classes  normalized applicableTo (never empty in practice)
 * @returns {object[]}
 */
/**
 * FIN-13484: the event class whose Group By keys a function uses, i.e. the second axis of the
 * `grouping_key_pairs` lookup `pairs[triggeredOn][feedingClass]`. Own-type when the function
 * aggregates its own trigger class (the legacy single-cell case); otherwise the one other class it
 * aggregates (cross-type: a transaction variant grouping activity history by `user_id`).
 *
 * @param {string[]} classes  normalized applicableTo
 * @param {string} triggeredOn  the variant's inputType
 * @returns {string}
 */
export const feedingClassFor = (classes, triggeredOn) => {
  const list = classes || [];
  if (list.length === 0 || list.includes(triggeredOn)) return triggeredOn;
  return list[0];
};

/**
 * FIN-13484: the field catalogue an aggregation / screening function may pick its aggregate field
 * and condition fields from. The BE requires each such field to exist on EVERY `applicableTo`
 * class (`FraudAggregationFunction.validate`, fieldsValidation / conditionValidation), so this is
 * the intersection of the per-class maps, not the union the drawer used to offer.
 *
 * @param {{transaction?: object, activity?: object}} fields  per-class `{ fieldId: meta }` maps
 * @param {string[]} classes  normalized applicableTo
 * @returns {object}
 */
export const fieldsFor = (fields, classes) => {
  const maps = (classes || []).map((c) => fields?.[c] || {});
  if (maps.length === 0) return {};
  const [first, ...rest] = maps;
  return Object.keys(first).reduce((acc, id) => {
    if (rest.every((m) => id in m)) acc[id] = first[id];
    return acc;
  }, {});
};

export const groupByOptionsFor = (groupByOptions, classes) => {
  const lists = (classes || []).map((c) => groupByOptions?.[c] || []);
  if (lists.length === 0) return [];
  const [first, ...rest] = lists;
  return first.filter((opt) =>
    rest.every((list) => list.some((o) => o.value === opt.value))
  );
};
